import axios from 'axios';

import BaseService from './BaseService';
import { url } from 'pages/Authorization';
import { getActorName } from 'shared/hooks/useActorName';

// Per-clip transcode state, polled while a folder's previews are being prepared.
export type PreviewStatus = 'ready' | 'transcoding' | 'error' | 'pending';

// One script with a creator upload folder — an option in the Sent scripts picker.
// One per storyboard.
export interface SentScript {
  storyboardId: string;
  scriptName: string | null;
  productName: string | null;
  deliveryCount: number;
  lastSentAt: string;
}

// One raw clip in a creator's Drive folder, with a short-lived signed URL the
// browser can play and scrub directly (the backend proxies the bytes from Drive).
export interface VideoClip {
  fileId: string;
  name: string;
  mimeType: string;
  sizeBytes: number | null;
  // Null right after upload, until Drive finishes processing the file.
  durationSec: number | null;
  width: number | null;
  height: number | null;
  createdTime: string | null;
  streamUrl: string;
}

// One "send to creator" of a script, when a script was sent to several creators
// and the editor must pick whose upload folder to work on.
export interface SourceDeliveryChoice {
  deliveryId: string;
  creatorId: string;
  creatorName: string | null;
  scriptName: string;
  driveFolderId: string;
  driveFolderUrl: string | null;
  createdAt: string;
}

// Result of resolving a source. `folderId` is present once we know the exact
// folder; for a multi-creator script not yet narrowed, only `deliveries` is set.
export interface ResolvedSource {
  // DROPBOX joined these when clips stopped having to live in Drive. Saved-plan types below
  // are deliberately NOT widened: a plan still records the Drive folder it was built from.
  sourceType: 'DRIVE' | 'DROPBOX' | 'STORYBOARD';
  folderId?: string;
  folderUrl?: string;
  storyboardId?: string;
  deliveryId?: string;
  deliveries?: SourceDeliveryChoice[];
}

export interface ClipsResponse {
  clips: VideoClip[];
  folderId?: string;
  // True when the folder holds more than the caller asked for. `total` is null in that case:
  // the backend stops counting as soon as it knows there is more, which is the point of asking
  // for a limit at all.
  truncated?: boolean;
  total?: number | null;
  // A multi-creator script that still needs a delivery picked.
  needsDeliveryChoice?: boolean;
  deliveries?: SourceDeliveryChoice[];
}

// A saved sub-clip: a named in/out range carved out of a longer source clip
// (video or audio). Current plans keep these inside their own timeline document;
// folderId remains so older folder-level records can be imported safely.
export interface ClipSegment {
  segmentId: string;
  folderId: string;
  sourceFileId: string;
  fileName: string | null;
  name: string | null;
  kind: 'video' | 'audio';
  inSec: number;
  outSec: number;
  createdAt?: string;
}

// One spoken span of a clip's transcript, overlaid on the clip's bar so you can
// see what is said where. `segments` are whisper's sentence-ish chunks; `words`
// are word-level timestamps (same call), so the UI can regroup captions freely.
export interface TranscriptSegment { startSec: number; endSec: number; text: string; }
export type TranscriptStatus = 'ready' | 'transcribing' | 'error' | 'pending';
export interface ClipTranscript {
  status: TranscriptStatus;
  error?: string;
  segments?: TranscriptSegment[];
  words?: TranscriptSegment[];
  // Where the real sound starts/ends in the source (ffmpeg silencedetect, widened
  // to whisper's word span) — drives auto-trimming leading/trailing silence.
  audioStartSec?: number;
  audioEndSec?: number;
}

// The resolved source the dashboard is working on — drives clip loading and which
// folder/script a saved edit plan is attached to.
export interface SourceContext {
  sourceType: 'DRIVE' | 'STORYBOARD';
  folderId: string;
  folderUrl?: string;
  storyboardId?: string;
  deliveryId?: string;
  label?: string;
}

// What the SourceSelector hands up when a source is ready. A pasted Drive source
// only knows the URL (the folderId comes back from listClips); a Drive source
// picked from the "recently used folders" list or a saved project already knows
// its concrete folderId, so it can skip re-resolving. `openPlanId`, when set, tells
// the timeline editor to open that specific saved project after clips load (rather
// than defaulting to the newest) — this is how a saved project opens independently.
export type LoadRequest =
  | { sourceType: 'DRIVE'; driveUrl: string; label: string; folderId?: string; folderUrl?: string; openPlanId?: string }
  | { sourceType: 'STORYBOARD'; folderId: string; folderUrl?: string; storyboardId: string; deliveryId?: string; label: string; openPlanId?: string };

// ── Multi-track timeline model (CapCut-style). Stored verbatim in the
// CI_VIDEO_EDIT_PLANS.TIMELINE VARIANT (schemaless) — no DB migration. The old
// beat model (TimelineEntry/AudioLayer) is gone; `version` distinguishes shapes.

// One clip placed on the single video track. Clips lay out sequentially (each
// starts where the previous ends), so a clip's timeline position is derived from
// the cumulative trimmed durations before it — not stored.
export interface TLVideoClip {
  id: string;               // instance uuid (splitting a clip creates more than one piece)
  sourceFileId: string;     // Drive fileId → VideoClip.streamUrl
  segmentId?: string;       // saved sub-clip identity; absent for the original source clip
  fileName: string;         // denormalized so the plan survives Drive renames
  sourceInSec: number;      // trim start within the source clip
  sourceOutSec: number;     // trim end within the source clip
  muteAudio: boolean;       // true = "replace" (an audio layer supplies sound); false = keep clip audio
  note?: string;            // editor note for this clip, e.g. on-screen text to add
}

// One picture-only clip on the B-roll overlay track, positioned freely like
// audio. Where it covers the timeline it REPLACES the main track's picture; it
// never contributes audio — the main clip underneath keeps playing its sound.
export interface TLOverlayClip {
  id: string;
  sourceFileId: string;
  segmentId?: string;
  fileName: string;
  sourceInSec: number;
  sourceOutSec: number;
  timelineStartSec: number; // where this cutaway starts on the timeline
  note?: string;            // editor note for this cutaway, e.g. on-screen text to add
}

// One clip on an audio layer, positioned freely along the timeline.
export interface TLAudioClip {
  id: string;
  sourceFileId: string;
  segmentId?: string;
  fileName: string;
  sourceInSec: number;
  sourceOutSec: number;
  timelineStartSec: number; // where this audio starts on the timeline
  note?: string;            // e.g. "duck under VO"
}

// One audio layer (music, VO, sfx). Several layers can stack.
export interface TLAudioTrack {
  id: string;
  name: string;
  clips: TLAudioClip[];
}

/**
 * A b-roll library clip pulled into a project.
 *
 * Only the id is authoritative — the clip detail is denormalized so the B-roll tab can render
 * before the resolve call returns (and still says something if the clip has since been deleted).
 * Signed stream/poster URLs are deliberately NOT stored: they expire, so they are re-minted on
 * every open via `resolveClipLabels`.
 */
export interface BrollAsset {
  labelId: string;
  fileId: string;
  fileName: string | null;
  name: string | null;
  inSec: number | null;
  outSec: number | null;
}

export interface EditorTimeline {
  version: 2;
  // The source clips shown in the Clips tab for this project. Older saved projects do
  // not have this field; the editor treats those as using every folder clip.
  sourceClipIds?: string[];
  // Saved and split clips owned by this project. Older projects do not have this
  // field; the editor imports their legacy folder-level records on read.
  clipSegments?: ClipSegment[];
  videoTrack: { clips: TLVideoClip[] };
  // B-roll cutaways layered above the video track. Optional: plans only gain the
  // field once a B-roll clip is added, so older documents stay byte-identical.
  overlayTrack?: { clips: TLOverlayClip[] };
  // Library clips pulled into this project — the B-roll tab's contents. Being assets rather
  // than placements, they sit here and not on a track: adding one makes it available to drag,
  // it does not put it in the edit.
  brollAssets?: BrollAsset[];
  audioTracks: TLAudioTrack[];
}

// Fresh empty timeline for a new plan.
export const emptyTimeline = (): EditorTimeline => ({
  version: 2,
  clipSegments: [],
  videoTrack: { clips: [] },
  audioTracks: [],
});

export interface EditPlan {
  planId: string;
  name: string | null;
  sourceType: 'DRIVE' | 'STORYBOARD';
  driveFolderId: string | null;
  driveFolderUrl: string | null;
  storyboardId: string | null;
  deliveryId: string | null;
  status: string;
  timeline: EditorTimeline;
  notes: string | null;
  // The project's ad script, used to generate hook ideas. Editable in the editor
  // and saved back here.
  script?: string | null;
  // The project's chosen opening on-screen-text hook and Meta ad headline.
  hook?: string | null;
  headline?: string | null;
  // The closing on-screen call-to-action, shown at the end of the video.
  cta?: string | null;
  // What kind of product the ad is for, from the same vocabulary the b-roll
  // library labels clips with (the `productCategory` clip-label dimension).
  productCategory?: string | null;
  // The editable brief that opens the generated instructions. Each section is stored
  // as one block of text the user edits directly, seeded from ../pages/VideoEditing/
  // briefTemplates; subtitles and music are a two-option choice, stored as the key.
  hookOverlay?: string | null;
  editingDirection?: string | null;
  finalCta?: string | null;
  subtitlesStyle?: string | null;
  musicStyle?: string | null;
  suggestStatus?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Header-only row for the "open a saved plan" list (no timeline). Carries enough
// source info (folder/url/storyboard/delivery) that picking a plan from the global
// "Saved projects" field can rebuild its SourceContext and load its clips, plus
// createdBy so the list can be grouped by who made each project.
export interface EditPlanSummary {
  planId: string;
  name: string | null;
  sourceType: 'DRIVE' | 'STORYBOARD';
  driveFolderId: string | null;
  driveFolderUrl: string | null;
  storyboardId: string | null;
  deliveryId: string | null;
  status: string;
  createdBy?: string | null;
  updatedAt: string;
}

// A project's lifecycle status, mirroring EDIT_PLAN_STATUS in the backend
// (sbly-dash-collector src/creative-intelligence/db/editPlans.js). Defined once
// because it is both written — the editor's Status select — and read: the project
// picker hides finished projects by default, and a literal that drifts on one side
// silently stops matching on the other.
export const EDIT_PLAN_STATUS = { DRAFT: 'DRAFT', READY: 'READY' } as const;

// Result of uploading assets into a folder — new clips that landed, plus any files
// that failed (e.g. one incompatible file in a batch) so the UI can report both.
export interface ClipUploadFailure { name: string; error: string; }
export interface ClipUploadResponse { uploaded: VideoClip[]; failed: ClipUploadFailure[]; }

// A Drive folder configured for automatic background transcription. `addedAt` is
// the ms watermark: only videos created after it are transcribed (existing ones are
// left alone). Null for a folder saved before watermarking / not yet stamped.
export interface AutoTranscribeFolder { folderId: string; folderUrl: string; addedAt: number | null; }

// Video Editing dashboard API. Unlike most services here, these methods let
// errors propagate (rather than swallowing to null) so the page can surface the
// backend's actionable message — e.g. "share this folder with <service account>".
// ── AI clip labels ──
// One whole-clip label per clip (scope 'clip'), plus one per sub-range the labeler
// split out (scope 'segment'). `labelSource` is 'human' once someone has corrected
// it, which stops a re-label run from overwriting the correction.

export interface ClipLabel {
  labelId: string;
  fileId: string;
  scope: 'clip' | 'segment';
  inSec: number | null;
  outSec: number | null;
  // The primary product classification — a broad category, not a Shopify SKU.
  productCategory: string | null;
  // Optional exact-SKU refinement; usually null until a person pins it.
  productHandle: string | null;
  productConfidence: 'high' | 'medium' | 'low' | 'none' | null;
  productReasoning: string | null;
  clipType: string | null;
  clipSubject: string[];
  keywords: string[];
  // Human-curated category slugs (see ClipCategory). Separate from the model's
  // vocabulary: people create these themselves and the labeler never fills them.
  categories: string[];
  // A person's title for the clip. null on anything the labeler produced.
  name: string | null;
  description: string | null;
  searchNotes: string | null;
  // Where the clip came from. Distinct from labelSource, which is who last EDITED it:
  // a corrected labeler sub-clip is labelSource 'human' but still creationSource
  // 'ai_label'. Legacy rows have no stored value and read as 'ai_label'.
  creationSource: CreationSource;
  model: string | null;
  labelSource: 'model' | 'human';
  editedBy: string | null;
  editedAt: string | null;
  // Share (0-1) of the labeled range with audible sound, measured with ffmpeg.
  // null means nobody has measured it yet.
  soundRatio: number | null;
  // Set once someone removes the clip from the library. Nothing is deleted.
  excludedAt: string | null;
  excludedBy: string | null;
  exclusionReason: string | null;
}

// Where a clip in the library came from.
export type CreationSource = 'ai_label' | 'video_editor' | 'library_trim' | 'library_import';

export const CREATION_SOURCE_LABEL: Record<CreationSource, string> = {
  ai_label: 'AI labelled',
  video_editor: 'Added from editor',
  library_trim: 'Trimmed here',
  library_import: 'From Drive',
};

// A category people curate by hand. `slug` is the immutable key stored on clips; `name`
// is the editable label, so renaming one never touches the clips filed under it.
export interface ClipCategory {
  categoryId: string;
  slug: string;
  name: string;
  createdBy: string | null;
  createdAt: string | null;
  archivedAt: string | null;
  labelCount: number;
}

// The editable fields, as the edit form posts them. Every key is optional — only
// what's sent gets changed, so a partial edit leaves the rest of the label alone.
export interface ClipLabelEdit {
  name?: string | null;
  productCategory?: string | null;
  productHandle?: string | null;
  productConfidence?: string;
  clipType?: string | null;
  clipSubject?: string[];
  categories?: string[];
  keywords?: string[];
  description?: string | null;
  searchNotes?: string | null;
  // Sent together by the trimmer. Rejected by the backend on a whole-take label, which
  // covers the entire clip by definition.
  inSec?: number;
  outSec?: number;
}

// Everything needed to add one clip to the library: which file, which range, plus any of
// the editable fields above.
export interface NewClipLabel extends ClipLabelEdit {
  fileId: string;
  inSec: number;
  outSec: number;
  creationSource: Exclude<CreationSource, 'ai_label'>;
}

export interface ClipLabelDimension {
  key: 'productCategory' | 'clipType' | 'clipSubject';
  label: string;
  enumType: string;
  cardinality: 'single' | 'multi';
  question: string;
  values: { value: string; description: string }[];
}

export interface ClipLabelProduct {
  handle: string;
  name: string | null;
  category: string | null;
  // Which productCategory this product falls into, so the specific-product
  // dropdown can be narrowed to the category already chosen.
  productCategory: string;
}

export interface ClipLabelVocabulary {
  dimensions: ClipLabelDimension[];
  productConfidence: string[];
  products: ClipLabelProduct[];
}

// A library search result: the label plus enough of its clip to show and play it.
// Which slice of the library to show. `broll` is the default: reusable footage
// only — nothing removed, nobody talking, and not mostly sound.
export type LibraryMode = 'broll' | 'all' | 'removed';

// Why a label is not reusable b-roll; empty when it is.
export type BrollDisqualifier = 'talking' | 'too_much_sound' | 'split' | 'removed';

export interface LibraryClipLabel extends ClipLabel {
  brollDisqualifiers: BrollDisqualifier[];
  // Still frame from the label's in-point, so the grid shows the footage rather
  // than a placeholder. null until the media backfill has run for this label.
  posterUrl: string | null;
  // How many sub-clips were carved out of this clip.
  segmentCount: number;
  fileName: string | null;
  durationSec: number | null;
  mimeType: string | null;
  sourceFolderId: string | null;
  // The edit project the clip's folder belongs to, when it belongs to one.
  planName: string | null;
  streamUrl: string;
}

export interface ClipLabelFacet { value: string; count: number; }
export interface ClipLabelFacets {
  productCategory: ClipLabelFacet[];
  clipType: ClipLabelFacet[];
  tags: ClipLabelFacet[];
  // Bare category slugs, not namespaced tags — the same values the filter sends back.
  categories: ClipLabelFacet[];
  creationSource: ClipLabelFacet[];
}

// What the library is currently filtered to. One object for search and facets alike, so
// the counts can never describe a different set than the results.
export interface LibraryFilters {
  mode?: LibraryMode;
  productCategory?: string | null;
  clipType?: string | null;
  tags?: string[];
  categories?: string[];
  creationSource?: CreationSource | null;
  q?: string;
  folderIds?: string[];
  limit?: number;
  offset?: number;
}

class VideoEditingService extends BaseService {
  private basePath = 'v1/video-editing';

  // Sent-script picker options. Reads the deliveries table directly (fast).
  // Pass refresh to bypass the server cache (the "Refresh" control).
  async listSentScripts(refresh = false): Promise<{ scripts: SentScript[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/sent-scripts`, { params: refresh ? { refresh: 'true' } : {} }),
      'Failed to list sent scripts',
    );
  }

  // The vocabulary + product mapping the label editor's dropdowns offer. Static per
  // deploy apart from the product list, so the page fetches it once.
  async getClipLabelVocabulary(): Promise<ClipLabelVocabulary | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/clip-labels/vocabulary`),
      'Failed to load clip label vocabulary',
    );
  }

  // One query string for /search and /facets. They MUST agree: each facet already
  // excludes its own dimension server-side so its options stay switchable, but sending
  // different filters to the two is how a count ends up advertising matches that
  // selecting it wouldn't return. Empty values are omitted rather than sent blank.
  private libraryParams(filters: LibraryFilters) {
    return {
      ...(filters.productCategory ? { productCategory: filters.productCategory } : {}),
      ...(filters.clipType ? { clipType: filters.clipType } : {}),
      ...(filters.tags?.length ? { tags: filters.tags.join(',') } : {}),
      ...(filters.categories?.length ? { categories: filters.categories.join(',') } : {}),
      ...(filters.creationSource ? { creationSource: filters.creationSource } : {}),
      ...(filters.q ? { q: filters.q } : {}),
      ...(filters.folderIds?.length ? { folderIds: filters.folderIds.join(',') } : {}),
      ...(filters.mode ? { mode: filters.mode } : {}),
    };
  }

  // The B-Roll Library search. Every filter is optional; they compose (AND between
  // filters, OR within one filter's values).
  async searchClipLabels(filters: LibraryFilters): Promise<{
    labels: LibraryClipLabel[];
    total: number;
    limit: number;
    offset: number;
    pageSizes: number[];
    mode: LibraryMode;
  } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/clip-labels/search`, {
        params: {
          ...this.libraryParams(filters),
          ...(filters.limit ? { limit: filters.limit } : {}),
          ...(filters.offset ? { offset: filters.offset } : {}),
        },
      }),
      'Failed to search the b-roll library',
    );
  }

  // Counts behind each filter option, so the library can show what it holds. Takes the
  // same filters as the search (limit/offset are irrelevant and ignored server-side).
  async getClipLabelFacets(filters: LibraryFilters = {}): Promise<ClipLabelFacets | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/clip-labels/facets`, {
        params: this.libraryParams(filters),
      }),
      'Failed to load library filters',
    );
  }

  /**
   * Re-mint the signed URLs for library clips a project references.
   *
   * A project stores only ids; the stream and poster URLs expire, so they cannot be saved with
   * it. `missing` names ids that no longer resolve — a deleted clip should leave one entry
   * flagged, not stop the project opening.
   */
  async resolveClipLabels(labelIds: string[]): Promise<{ labels: LibraryClipLabel[]; missing: string[] } | null> {
    if (!labelIds.length) return { labels: [], missing: [] };
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/clip-labels/resolve`, { params: { labelIds: labelIds.join(',') } }),
      'Failed to load the b-roll clips this project uses',
    );
  }

  // ── The human category vocabulary ──
  // Created from the UI as people need them, so this is read fresh rather than cached
  // with the (static) label vocabulary.
  async listClipCategories(): Promise<{ categories: ClipCategory[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/clip-labels/categories`),
      'Failed to load clip categories',
    );
  }

  // Errors propagate: "that name is already taken" has to reach the person typing it.
  async createClipCategory(name: string): Promise<{ category: ClipCategory }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/clip-labels/categories`, {
      name,
      createdBy: getActorName() || undefined,
    });
    return resp.data as { category: ClipCategory };
  }

  // Renames the display label only; the slug clips carry never moves, so nothing is
  // re-tagged and no clip loses its category.
  async renameClipCategory(categoryId: string, name: string): Promise<{ category: ClipCategory }> {
    await this._getAccessToken();
    const resp = await this.http.put(
      `${this.basePath}/clip-labels/categories/${encodeURIComponent(categoryId)}`,
      { name },
    );
    return resp.data as { category: ClipCategory };
  }

  // Retire a category: out of the picker, still shown on clips already filed under it.
  async archiveClipCategory(categoryId: string): Promise<{ category: ClipCategory }> {
    await this._getAccessToken();
    const resp = await this.http.delete(
      `${this.basePath}/clip-labels/categories/${encodeURIComponent(categoryId)}`,
    );
    return resp.data as { category: ClipCategory };
  }

  // Remove one clip (or sub-clip) from the library — near-duplicate coverage, or
  // footage that isn't useful. Reversible; nothing is deleted.
  async excludeClipLabel(labelId: string, reason: string): Promise<{ label: ClipLabel }> {
    await this._getAccessToken();
    const resp = await this.http.post(
      `${this.basePath}/clip-labels/${encodeURIComponent(labelId)}/exclude`,
      { reason, excludedBy: getActorName() || undefined },
    );
    return resp.data as { label: ClipLabel };
  }

  async restoreClipLabel(labelId: string): Promise<{ label: ClipLabel }> {
    await this._getAccessToken();
    const resp = await this.http.delete(`${this.basePath}/clip-labels/${encodeURIComponent(labelId)}/exclude`);
    return resp.data as { label: ClipLabel };
  }

  async listClipLabels(folderId: string): Promise<{ labels: ClipLabel[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/clip-labels`, { params: { folderId } }),
      'Failed to load clip labels',
    );
  }

  // Save an editor's corrections to one label. Errors propagate so the form can show
  // the backend's validation message rather than a generic failure.
  async updateClipLabel(labelId: string, edit: ClipLabelEdit): Promise<{ label: ClipLabel }> {
    await this._getAccessToken();
    const resp = await this.http.put(
      `${this.basePath}/clip-labels/${encodeURIComponent(labelId)}`,
      { ...edit, editedBy: getActorName() || undefined },
    );
    return resp.data as { label: ClipLabel };
  }

  /**
   * Add one clip to the b-roll library — a named range of a source file.
   *
   * Backs both "Add to B-Roll library" in the editor and "Save as new clip" in the
   * library; they differ only in `creationSource`. Errors propagate: the useful failures
   * here are specific ("open its folder in the Video Editing tool first") and the person
   * saving needs to read them.
   */
  async createClipLabel(input: NewClipLabel): Promise<{ label: LibraryClipLabel }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/clip-labels`, {
      ...input,
      createdBy: getActorName() || undefined,
    });
    return resp.data as { label: LibraryClipLabel };
  }

  async resolveSource(params: {
    driveUrl?: string;
    storyboardId?: string;
    deliveryId?: string;
  }): Promise<ResolvedSource> {
    await this._getAccessToken();
    const resp = await this.http.get(`${this.basePath}/sources/resolve`, { params });
    return resp.data as ResolvedSource;
  }

  // Pre-warm (start=true) + poll per-clip transcode status. Pass only video
  // fileIds — audio plays natively and needs no transcode.
  async getPreviewStatus(fileIds: string[], start = false): Promise<{
    statuses: Record<string, PreviewStatus>;
    errors: Record<string, string>;
  } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/clips/preview-status`, {
        params: { fileIds: fileIds.join(','), ...(start ? { start: 'true' } : {}) },
      }),
      'Failed to get preview status',
    );
  }

  async retryClipPreview(fileId: string): Promise<{ status: PreviewStatus }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/clips/${encodeURIComponent(fileId)}/preview/retry`);
    return resp.data as { status: PreviewStatus };
  }

  // Poll speech transcripts for clips (start=true also enqueues missing ones).
  async getClipTranscripts(fileIds: string[], start = false): Promise<{ transcripts: Record<string, ClipTranscript> } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/clips/transcripts`, {
        params: { fileIds: fileIds.join(','), ...(start ? { start: 'true' } : {}) },
      }),
      'Failed to get clip transcripts',
    );
  }

  // Split a video clip's audio into its own file in the same Drive folder
  // ("<name> (audio).m4a"). The backend reuses the video's finished transcript
  // for the new file (no second whisper job) and returns the existing file if
  // the audio was extracted before. Requires the clip's preview to be ready.
  // Errors propagate so the caller can surface the backend's message.
  async extractClipAudio(fileId: string, folderId: string): Promise<{
    clip: VideoClip;
    alreadyExisted: boolean;
    transcriptCopied: boolean;
  }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/clips/${encodeURIComponent(fileId)}/extract-audio`, { folderId });
    return resp.data as { clip: VideoClip; alreadyExisted: boolean; transcriptCopied: boolean };
  }

  async retryClipTranscript(fileId: string): Promise<{ status: TranscriptStatus }> {
    await this._getAccessToken();
    const resp = await this.http.post(`${this.basePath}/clips/${encodeURIComponent(fileId)}/transcript/retry`);
    return resp.data as { status: TranscriptStatus };
  }

  // ── Legacy folder-level saved clip segments ──
  // Current plans persist these inside EditorTimeline; this API remains so
  // plans created before plan isolation can import their existing clips.

  async listClipSegments(folderId: string): Promise<{ segments: ClipSegment[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/clip-segments`, { params: { folderId } }),
      'Failed to list clip segments',
    );
  }

  async createClipSegment(body: {
    folderId: string;
    sourceFileId: string;
    fileName?: string | null;
    name?: string | null;
    kind: 'video' | 'audio';
    inSec: number;
    outSec: number;
  }): Promise<{ segment: ClipSegment } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/clip-segments`, body),
      'Failed to save clip segment',
    );
  }

  async splitClipSegment(body: {
    folderId: string;
    sourceFileId: string;
    fileName?: string | null;
    name?: string | null;
    kind: 'video' | 'audio';
    inSec: number;
    splitSec: number;
    outSec: number;
  }): Promise<{ segments: [ClipSegment, ClipSegment] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/clip-segments/split`, body),
      'Failed to split clip segment',
    );
  }

  async updateClipSegment(segmentId: string, body: {
    inSec: number;
    outSec: number;
  }): Promise<{ segment: ClipSegment } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/clip-segments/${segmentId}`, body),
      'Failed to update clip segment',
    );
  }

  async deleteClipSegment(segmentId: string): Promise<{ ok: boolean } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.delete(`${this.basePath}/clip-segments/${segmentId}`),
      'Failed to delete clip segment',
    );
  }

  async listClips(params: {
    folderId?: string;
    // A pasted folder link. `driveUrl` is the original name and still works; `sourceUrl` is
    // the one to use now that a link can be Google Drive or Dropbox.
    sourceUrl?: string;
    driveUrl?: string;
    // Only for a Dropbox link that has one. Sent as a header, not a query parameter: this is
    // a GET, and a query string is written to access logs and browser history.
    password?: string;
    storyboardId?: string;
    deliveryId?: string;
    refresh?: boolean;
    // Show at most this many. Omit for everything — the video editor must, because a saved
    // plan references clips by id and a truncated listing would leave a timeline unable to
    // resolve its own footage.
    limit?: number;
  }): Promise<ClipsResponse> {
    await this._getAccessToken();
    const { refresh, password, ...rest } = params;
    const resp = await this.http.get(`${this.basePath}/clips`, {
      params: refresh ? { ...rest, refresh: 'true' } : rest,
      ...(password ? { headers: { 'X-Source-Password': password } } : {}),
    });
    return resp.data as ClipsResponse;
  }

  // Upload video/audio assets into a Drive folder (multipart). Unlike the edit-plan
  // methods this lets errors propagate (like listClips), so the page can surface the
  // backend's actionable message — e.g. a 403 "share the folder as Editor". Reports
  // upload progress (0–100) via onProgress.
  async uploadClips(
    folderId: string,
    files: File[],
    onProgress?: (percent: number) => void,
  ): Promise<ClipUploadResponse> {
    await this._getAccessToken();
    const form = new FormData();
    form.append('folderId', folderId);
    files.forEach((f) => form.append('files', f));
    const resp = await this.http.post(`${this.basePath}/clips/upload`, form, {
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return resp.data as ClipUploadResponse;
  }

  // ── Edit plans (Phase 2) — these swallow to null on error (BaseService
  // pattern); the editor treats null as "save/load failed" in its status UI.

  async createEditPlan(body: {
    name?: string | null;
    sourceType: 'DRIVE' | 'STORYBOARD';
    driveFolderId: string;
    driveFolderUrl?: string;
    storyboardId?: string;
    deliveryId?: string;
    timeline?: EditorTimeline;
    notes?: string | null;
  }): Promise<{ plan: EditPlan } | null> {
    await this._getAccessToken();
    // Stamp the current dashboard user (top-bar selector / Watchdog actor) so the
    // project is attributed to whoever created it. Empty → let the server store null.
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/edit-plans`, { ...body, createdBy: getActorName() || undefined }),
      'Failed to create project',
    );
  }

  async listEditPlans(params: { storyboardId?: string; folderId?: string }): Promise<{ plans: EditPlanSummary[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/edit-plans`, { params }),
      'Failed to list projects',
    );
  }

  // Every saved project across all sources/users (no source filter), newest first —
  // powers the global "Saved projects" picker so projects load independently of
  // first locating their assets.
  async listAllEditPlans(): Promise<{ plans: EditPlanSummary[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/edit-plans`),
      'Failed to list projects',
    );
  }

  async getEditPlanById(planId: string): Promise<{ plan: EditPlan } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/edit-plans/${planId}`),
      'Failed to load project',
    );
  }

  async updateEditPlan(planId: string, body: {
    name?: string | null;
    status?: string;
    notes?: string | null;
    script?: string | null;
    hook?: string | null;
    headline?: string | null;
    cta?: string | null;
    productCategory?: string | null;
    hookOverlay?: string | null;
    editingDirection?: string | null;
    finalCta?: string | null;
    subtitlesStyle?: string | null;
    musicStyle?: string | null;
    timeline: EditorTimeline;
  }): Promise<{ plan: EditPlan } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/edit-plans/${planId}`, body),
      'Failed to save project',
    );
  }

  async deleteEditPlan(planId: string): Promise<{ ok: boolean } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.delete(`${this.basePath}/edit-plans/${planId}`),
      'Failed to delete project',
    );
  }

  // The editor instructions. Despite the key, the body is PLAIN TEXT laid out with
  // rules and fixed-width columns — show it in a monospace block, don't render it
  // as markdown. (The key is kept for compatibility with the deployed API.)
  async getInstructions(planId: string): Promise<{ markdown: string } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/edit-plans/${planId}/instructions`),
      'Failed to render instructions',
    );
  }

  // PUBLIC read-only preview of a plan's timeline, for the shareable no-login
  // link. Sends NO auth token (the caller may be logged out); the signed
  // (exp/sig) link is the access gate, verified server-side. Errors propagate so
  // the preview page can distinguish expired (410) / not-found (404) links.
  async getPublicPreview(planId: string, exp: string, sig: string): Promise<{
    plan: { name: string | null; timeline: EditorTimeline };
    clips: { fileId: string; streamUrl: string }[];
  }> {
    const resp = await axios.get(`${url}/${this.basePath}-preview/${encodeURIComponent(planId)}`, {
      params: { exp, sig },
    });
    return resp.data;
  }

  // Draft ad-hook ideas (headlines + captions) for the opening of the video from
  // its script, using the editor-chosen prompt and model (routed to Anthropic or
  // OpenRouter server-side). Returns the raw suggestions for the editor to show.
  async generateHooks(body: { prompt: string; model: string }): Promise<{ text: string; model: string } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/hooks`, body),
      'Failed to generate hook ideas',
    );
  }


  // ── Auto-transcribe folders (dashboard gear setting) ──
  // The Drive folders whose new clips get transcribed automatically by the
  // reconciler sweep. Stored server-side (Firebase settings), read by the worker.

  async getAutoTranscribeFolders(): Promise<{ folders: AutoTranscribeFolder[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/auto-transcribe-folders`),
      'Failed to load auto-transcribe folders',
    );
  }

  // Send Drive folder links or ids; the server parses/dedupes and returns the saved list.
  async setAutoTranscribeFolders(folders: string[]): Promise<{ folders: AutoTranscribeFolder[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/auto-transcribe-folders`, { folders }),
      'Failed to save auto-transcribe folders',
    );
  }
}

export default VideoEditingService;
